import { useEffect, useMemo, useState } from 'react';
import { useCurrencies } from '@features/projects';
import { Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { Info, Play } from 'lucide-react';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import {
  adjustmentAmountForMode,
  caseStatusLabel,
  caseTypeLabel,
  decisionActionLabel,
  formatReconciliationAge,
  isLedgerDifferenceCase,
  NetworkCostPolicyTableRow,
  parseWalletIdFromScopeKey,
  ReconciliationRunStatusBadge,
  suspenseStatusLabel,
  suspenseTypeLabel,
  costStateLabel,
  policyOperationLabel,
  policyPayerLabel,
  buildCurrencyIconMap,
  resolveCurrencyIconSrc,
} from '@entities/reconciliation';
import { createIdempotencyKey } from '@entities/withdrawal';
import {
  formatReconciliationAssigneeLabel,
  runSecureReconciliationCommand,
  reconcileNetworkAfterAssetChange,
  useReconciliationAssignees,
  useReconciliationAssetNetworkOptions,
  useReconciliationDiscrepanciesList,
  useReconciliationNetworkCostsList,
  useReconciliationRunExport,
  useReconciliationRunRowsList,
  useReconciliationRunsList,
  useReconciliationSuspenseList,
} from '@features/reconciliation';
import { useSecondFactorAction } from '@features/security';
import type {
  NetworkCostsQuery,
  ReconciliationAdjustmentMode,
  ReconciliationCasesQuery,
  ReconciliationDecisionDto,
  ReconciliationRunRowsQuery,
  ReconciliationRunsQuery,
  SuspenseListQuery,
} from '@shared/api/contracts';
import { swrKeys } from '@shared/api/swr/keys';
import {
  reconciliationCasesTableClass,
  reconciliationCostsTableClass,
  reconciliationPoliciesTableClass,
  reconciliationRunsTableClass,
  reconciliationSuspenseTableClass,
} from '@widgets/reconciliation/lib/table-classes';
import {
  DiscrepancyAdjustmentDialog,
  DiscrepancyAssignDialog,
  DiscrepancyCommentDialog,
  ReconciliationEnqueueDialog,
  ReconciliationAssetCell,
  ReconciliationAssetNetworkFilter,
  ReconciliationAssigneeFilter,
  ReconciliationFilterInput,
  ReconciliationFilterSelect,
  ReconciliationListControls,
  ReconciliationRunDetailDialog,
  ReconciliationTablePagination,
  ReconciliationTableShell,
  type ReconciliationRunRowsFilterDraft,
  SuspenseAssignDialog,
  useReconciliationFilterOptions,
} from '@widgets/reconciliation';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { formatDate, formatDateTime } from '@shared/lib/format-date';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { useApiServices } from '@shared/lib/use-api-services';
import {
  Button,
  CabinetTablePageSkeleton,
  ReasonFactorDialog,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsBadge,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@shared/ui';

const POLL_MS = 15000;

export function ReconciliationPage() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const services = useApiServices();
  const { run: runWithFactor } = useSecondFactorAction();
  const canRead = hasPermission(user, 'reconciliation.read');
  const canInvestigate = hasPermission(user, 'reconciliation.investigate');
  const canResolve = hasPermission(user, 'reconciliation.resolve');
  const canAssignSuspense = hasPermission(user, 'suspense.assign');
  const canManagePolicies = hasPermission(user, 'network_cost_policy.manage');
  const { currencies, isLoading: currenciesLoading } = useCurrencies();
  const currencyIconMap = useMemo(() => buildCurrencyIconMap(currencies), [currencies]);
  const {
    assignees: reconciliationAssignees,
    isLoading: assigneesLoading,
    error: assigneesError,
  } = useReconciliationAssignees(canRead);

  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'runs';
  const runId = params.get('runId');
  const caseId = params.get('caseId');
  const [runsDraft, setRunsDraft] = useState<ReconciliationRunsQuery>({});
  const [runsFilters, setRunsFilters] = useState<ReconciliationRunsQuery>({});
  const [casesDraft, setCasesDraft] = useState<ReconciliationCasesQuery>({});
  const [casesFilters, setCasesFilters] = useState<ReconciliationCasesQuery>({});
  const [suspenseDraft, setSuspenseDraft] = useState<SuspenseListQuery>({});
  const [suspenseFilters, setSuspenseFilters] = useState<SuspenseListQuery>({});
  const [costsDraft, setCostsDraft] = useState<NetworkCostsQuery>({});
  const [costsFilters, setCostsFilters] = useState<NetworkCostsQuery>({});
  const [rowFiltersDraft, setRowFiltersDraft] = useState<ReconciliationRunRowsFilterDraft>({});
  const [rowFilters, setRowFilters] = useState<ReconciliationRunRowsQuery>({});
  const [runsPage, setRunsPage] = useState(1);
  const [casesPage, setCasesPage] = useState(1);
  const [suspensePage, setSuspensePage] = useState(1);
  const [costsPage, setCostsPage] = useState(1);
  const [runRowsPage, setRunRowsPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [enqueueOpen, setEnqueueOpen] = useState(false);
  const [enqueueReason, setEnqueueReason] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [assignSuspenseOpen, setAssignSuspenseOpen] = useState(false);
  const [assignFactorOpen, setAssignFactorOpen] = useState(false);
  const [suspenseId, setSuspenseId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [caseAssignOpen, setCaseAssignOpen] = useState(false);
  const [caseAssignee, setCaseAssignee] = useState('');
  const [caseAssignReason, setCaseAssignReason] = useState('');
  const [caseCommentOpen, setCaseCommentOpen] = useState(false);
  const [caseCommentText, setCaseCommentText] = useState('');
  const [caseCommentReason, setCaseCommentReason] = useState('');
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [adjustmentWalletId, setAdjustmentWalletId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentMode, setAdjustmentMode] = useState<ReconciliationAdjustmentMode>('RESTORE_PROJECTION');
  const [adjustmentEvidence, setAdjustmentEvidence] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentPreview, setAdjustmentPreview] = useState<ReconciliationDecisionDto | null>(null);
  const [adjustmentFactorOpen, setAdjustmentFactorOpen] = useState(false);

  const invalidateAdjustmentPreview = () => setAdjustmentPreview(null);
  const filterOptions = useReconciliationFilterOptions();
  const assetNetworkFilterOptions = useReconciliationAssetNetworkOptions(currencies);
  const withAssetNetwork = (
    assetCode: string | undefined,
    networkCode: string | undefined,
  ) =>
    reconcileNetworkAfterAssetChange(
      assetCode,
      networkCode,
      assetNetworkFilterOptions.networkOptionsForAsset,
    );
  const runExport = useReconciliationRunExport(runId, rowFilters);
  const runsList = useReconciliationRunsList(runsFilters, runsPage, canRead);
  const discrepanciesList = useReconciliationDiscrepanciesList(casesFilters, casesPage, canRead);
  const suspenseList = useReconciliationSuspenseList(suspenseFilters, suspensePage, canRead);
  const costsList = useReconciliationNetworkCostsList(costsFilters, costsPage, canRead);
  const runRowsList = useReconciliationRunRowsList(runId, rowFilters, runRowsPage, canRead && Boolean(runId));
  const runDetail = useSWR(
    canRead && runId ? swrKeys.reconciliation.run(runId) : null,
    () => services.reconciliation.getRun(runId!),
    {
      refreshInterval: (data) =>
        data?.status === 'QUEUED' || data?.status === 'RUNNING' ? POLL_MS : 0,
    },
  );
  const caseDetail = useSWR(canRead && caseId ? swrKeys.reconciliation.discrepancy(caseId) : null, () =>
    services.reconciliation.getDiscrepancy(caseId!),
  );
  const policies = useSWR(canRead && canManagePolicies ? swrKeys.reconciliation.policies() : null, () =>
    services.reconciliation.listPolicies(),
  );

  useEffect(() => {
    setRowFiltersDraft({});
    setRowFilters({});
    setRunRowsPage(1);
  }, [runId]);

  const setTab = (next: string) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', next);
    copy.delete('runId');
    copy.delete('caseId');
    setParams(copy);
  };

  const openRun = (id: string) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', 'runs');
    copy.set('runId', id);
    setParams(copy);
  };

  const openCase = (id: string) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', 'discrepancies');
    copy.set('caseId', id);
    setParams(copy);
  };

  const closeSheet = () => {
    const copy = new URLSearchParams(params);
    copy.delete('runId');
    copy.delete('caseId');
    setParams(copy);
  };

  const enqueueRun = async () => {
    if (enqueueReason.trim().length < 3) return;
    setBusy(true);
    try {
      await services.reconciliation.enqueueRun({
        reason: enqueueReason.trim(),
        idempotencyKey: createIdempotencyKey(),
      });
      toast(t('reconciliation.enqueue.success'), 'success');
      setEnqueueOpen(false);
      setEnqueueReason('');
      setRunsPage(1);
      void runsList.refresh();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.enqueue.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportRun = async () => {
    try {
      await runExport.exportCsv();
      toast(t('reconciliation.export.done'), 'success');
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.export.error'), 'error');
    }
  };

  const resolveCase = async (value: { reason: string; secondFactor: { method: 'totp' | 'backup' | 'email'; code: string } }) => {
    const currentCase = caseDetail.data;
    if (!caseId || !currentCase) return;
    setBusy(true);
    try {
      const result = await runWithFactor({
        purpose: t('reconciliation.resolve.purpose'),
        required: true,
        request: async (factor) =>
          runSecureReconciliationCommand({
            reconciliation: services.reconciliation,
            action: 'resolve',
            targetKey: caseId,
            reason: value.reason,
            evidenceRefs: ['ui:reconciliation-resolve'],
            secondFactor: factor,
            businessPayload: {
              expectedVersion: currentCase.version,
              resolutionKind: 'RECONCILED',
            },
            execute: (body) => services.reconciliation.resolveDiscrepancy(caseId, body as never),
          }),
      });
      if (!result.ok) return;
      toast(t('reconciliation.resolve.success'), 'success');
      setResolveOpen(false);
      void caseDetail.mutate();
      setCasesPage(1);
      void discrepanciesList.refresh();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.resolve.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const parseEvidenceRefs = (raw: string) =>
    raw
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter((item) => item.length >= 3);

  const openCaseAssign = () => {
    const selfId = user?.id ? Number(user.id) : null;
    const defaultAssignee =
      selfId != null && reconciliationAssignees.some((admin) => admin.id === selfId)
        ? String(selfId)
        : reconciliationAssignees[0]
          ? String(reconciliationAssignees[0].id)
          : selfId != null
            ? String(selfId)
            : '';
    setCaseAssignee(defaultAssignee);
    setCaseAssignReason('');
    setCaseAssignOpen(true);
  };

  const assignCase = async () => {
    const currentCase = caseDetail.data;
    const assignee = Number.parseInt(caseAssignee.trim(), 10);
    if (!caseId || !currentCase || !Number.isSafeInteger(assignee) || assignee < 1 || caseAssignReason.trim().length < 3) return;
    setBusy(true);
    try {
      await services.reconciliation.assignDiscrepancy(caseId, {
        assignee,
        reason: caseAssignReason.trim(),
        idempotencyKey: createIdempotencyKey(),
        expectedVersion: currentCase.version,
      });
      toast(t('reconciliation.case.assign.success'), 'success');
      setCaseAssignOpen(false);
      void caseDetail.mutate();
      setCasesPage(1);
      void discrepanciesList.refresh();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.case.assign.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const commentCase = async () => {
    const currentCase = caseDetail.data;
    if (!caseId || !currentCase || caseCommentText.trim().length < 3 || caseCommentReason.trim().length < 3) return;
    setBusy(true);
    try {
      await services.reconciliation.commentDiscrepancy(caseId, {
        text: caseCommentText.trim(),
        reason: caseCommentReason.trim(),
        idempotencyKey: createIdempotencyKey(),
        expectedVersion: currentCase.version,
      });
      toast(t('reconciliation.case.comment.success'), 'success');
      setCaseCommentOpen(false);
      setCaseCommentText('');
      setCaseCommentReason('');
      void caseDetail.mutate();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.case.comment.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const openAdjustment = () => {
    const currentCase = caseDetail.data;
    if (!currentCase) return;
    const wallet = parseWalletIdFromScopeKey(currentCase.scopeKey);
    const mode: ReconciliationAdjustmentMode = 'RESTORE_PROJECTION';
    setAdjustmentMode(mode);
    setAdjustmentWalletId(wallet ? String(wallet) : '');
    setAdjustmentAmount(adjustmentAmountForMode(currentCase.amount, mode));
    setAdjustmentEvidence('');
    setAdjustmentReason('');
    setAdjustmentPreview(null);
    setAdjustmentOpen(true);
  };

  const previewAdjustment = async () => {
    const currentCase = caseDetail.data;
    const walletId = Number.parseInt(adjustmentWalletId.trim(), 10);
    const evidenceRefs = parseEvidenceRefs(adjustmentEvidence);
    if (!caseId || !currentCase || !Number.isSafeInteger(walletId) || evidenceRefs.length === 0 || adjustmentReason.trim().length < 3) return;
    setBusy(true);
    try {
      const preview = await services.reconciliation.previewAdjustment(caseId, {
        amount: adjustmentAmount.trim(),
        assetCode: currentCase.assetCode,
        networkCode: currentCase.networkCode,
        walletId,
        mode: adjustmentMode,
        evidenceRefs,
        reason: adjustmentReason.trim(),
        idempotencyKey: createIdempotencyKey(),
        expectedVersion: currentCase.version,
      });
      setAdjustmentPreview(preview);
      void caseDetail.mutate();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.case.adjustment.previewError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmAdjustment = async (value: { reason: string; secondFactor: { method: 'totp' | 'backup' | 'email'; code: string } }) => {
    const currentCase = caseDetail.data;
    const preview = adjustmentPreview;
    if (!caseId || !currentCase || !preview) return;
    const evidenceFromPreview =
      preview.evidence && typeof preview.evidence === 'object' && Array.isArray((preview.evidence as { refs?: string[] }).refs)
        ? (preview.evidence as { refs: string[] }).refs
        : parseEvidenceRefs(adjustmentEvidence);
    if (!evidenceFromPreview.length) return;
    setBusy(true);
    try {
      const result = await runWithFactor({
        purpose: t('reconciliation.case.adjustment.confirmPurpose'),
        required: true,
        request: async (factor) =>
          runSecureReconciliationCommand({
            reconciliation: services.reconciliation,
            action: 'ADJUSTMENT',
            targetKey: caseId,
            reason: value.reason,
            evidenceRefs: evidenceFromPreview,
            secondFactor: factor,
            businessPayload: {
              expectedVersion: currentCase.version,
              previewId: preview.id,
              evidenceRefs: evidenceFromPreview,
            },
            execute: (body) => services.reconciliation.confirmAdjustment(caseId, body as never),
          }),
      });
      if (!result.ok) return;
      toast(t('reconciliation.case.adjustment.success'), 'success');
      setAdjustmentFactorOpen(false);
      setAdjustmentOpen(false);
      setAdjustmentPreview(null);
      void caseDetail.mutate();
      setCasesPage(1);
      void discrepanciesList.refresh();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.case.adjustment.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const assignSuspense = async (value: { reason: string; secondFactor: { method: 'totp' | 'backup' | 'email'; code: string } }) => {
    const targetId = suspenseId ?? params.get('suspenseId');
    if (!targetId || !invoiceId.trim()) return;
    setBusy(true);
    try {
      const detail = await services.reconciliation.getSuspense(targetId);
      const result = await runWithFactor({
        purpose: t('reconciliation.suspense.assignPurpose'),
        required: true,
        request: async (factor) =>
          runSecureReconciliationCommand({
            reconciliation: services.reconciliation,
            action: 'SUSPENSE_ASSIGN',
            targetKey: targetId,
            reason: value.reason || assignReason.trim(),
            evidenceRefs: ['ui:suspense-assign'],
            secondFactor: factor,
            businessPayload: {
              expectedVersion: detail.version,
              targetInvoiceId: invoiceId.trim(),
            },
            execute: (body) => services.reconciliation.assignSuspense(targetId, body as never),
          }),
      });
      if (!result.ok) return;
      toast(t('reconciliation.suspense.assignSuccess'), 'success');
      setAssignFactorOpen(false);
      setAssignSuspenseOpen(false);
      setInvoiceId('');
      setAssignReason('');
      setSuspenseId(null);
      setSuspensePage(1);
      void suspenseList.refresh();
    } catch (error) {
      toast(getApiErrorMessage(error, t, 'reconciliation.suspense.assignError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const runItems = runsList.items;
  const caseItems = discrepanciesList.items;
  const suspenseItems = suspenseList.items;
  const costItems = costsList.items;
  const policyItems = policies.data?.data ?? [];

  const criticalCount = useMemo(
    () => discrepanciesList.items.filter((item) => item.status !== 'RESOLVED').length,
    [discrepanciesList.items],
  );

  const applyRunsFilters = () => {
    setRunsPage(1);
    setRunsFilters({ ...runsDraft });
  };
  const resetRunsFilters = () => {
    setRunsDraft({});
    setRunsPage(1);
    setRunsFilters({});
  };

  const applyCasesFilters = () => {
    setCasesPage(1);
    setCasesFilters({
      status: casesDraft.status,
      type: casesDraft.type,
      assetCode: casesDraft.assetCode,
      networkCode: casesDraft.networkCode,
      assignee: casesDraft.assignee,
    });
  };
  const resetCasesFilters = () => {
    setCasesDraft({});
    setCasesPage(1);
    setCasesFilters({});
  };

  const applySuspenseFilters = () => {
    setSuspensePage(1);
    setSuspenseFilters({ ...suspenseDraft });
  };
  const resetSuspenseFilters = () => {
    setSuspenseDraft({});
    setSuspensePage(1);
    setSuspenseFilters({});
  };

  const applyCostsFilters = () => {
    setCostsPage(1);
    setCostsFilters({
      state: costsDraft.state,
      operationType: costsDraft.operationType,
      assetCode: costsDraft.assetCode,
    });
  };
  const resetCostsFilters = () => {
    setCostsDraft({});
    setCostsPage(1);
    setCostsFilters({});
  };

  const applyRowFilters = () => {
    setRunRowsPage(1);
    setRowFilters({
      assetCode: rowFiltersDraft.assetCode,
      networkCode: rowFiltersDraft.networkCode,
    });
  };
  const resetRowFilters = () => {
    setRowFiltersDraft({});
    setRunRowsPage(1);
    setRowFilters({});
  };

  if (authLoading) return <CabinetTablePageSkeleton showInfoBanner tableRows={8} />;
  if (!canRead) return <Navigate to="/balance" replace />;

  return (
    <div className="page-stack min-w-0">
      <div className="page-info-banner grid grid-cols-[auto_1fr] items-start gap-2.5 p-3 sm:gap-3 sm:p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-foreground">{t('reconciliation.info.title')}</p>
          <p className="mt-0.5 text-muted-foreground">{t('reconciliation.info.body')}</p>
        </div>
      </div>

      <div className="surface-card space-y-4">
        <div className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <p className="min-w-0 font-sans text-sm leading-snug text-foreground sm:pr-4 sm:flex-1">
            {t('reconciliation.toolbar.hint')}
          </p>
          {canInvestigate ? (
            <Button
              type="button"
              variant="default"
              className="page-action-button w-full shrink-0 sm:w-auto sm:max-w-xs"
              onClick={() => setEnqueueOpen(true)}
              disabled={busy}
            >
              <Play className="mr-1.5 h-4 w-4" aria-hidden />
              {t('reconciliation.enqueue.action')}
            </Button>
          ) : null}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl p-1 sm:rounded-2xl">
          <TabsTrigger value="runs" className="flex-none px-3">
            {t('reconciliation.tabs.runs')}
          </TabsTrigger>
          <TabsTrigger value="discrepancies" className="flex-none px-3">
            {t('reconciliation.tabs.discrepancies')}
            {criticalCount > 0 ? <TabsBadge>{criticalCount}</TabsBadge> : null}
          </TabsTrigger>
          <TabsTrigger value="suspense" className="flex-none px-3">
            {t('reconciliation.tabs.suspense')}
          </TabsTrigger>
          <TabsTrigger value="costs" className="flex-none px-3">
            {t('reconciliation.tabs.costs')}
          </TabsTrigger>
          {canManagePolicies ? (
            <TabsTrigger value="policies" className="flex-none px-3">
              {t('reconciliation.tabs.policies')}
            </TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="runs" className="mt-4 space-y-3">
          <ReconciliationListControls onApply={applyRunsFilters} onReset={resetRunsFilters}>
            <ReconciliationFilterSelect
              id="runs-filter-status"
              label={t('reconciliation.filters.status')}
              value={runsDraft.status}
              onChange={(status) => setRunsDraft((prev) => ({ ...prev, status }))}
              options={filterOptions.runStatusOptions}
            />
            <ReconciliationFilterInput
              id="runs-filter-date-from"
              label={t('reconciliation.filters.dateFrom')}
              value={runsDraft.dateFrom}
              onChange={(dateFrom) => setRunsDraft((prev) => ({ ...prev, dateFrom }))}
            />
            <ReconciliationFilterInput
              id="runs-filter-date-to"
              label={t('reconciliation.filters.dateTo')}
              value={runsDraft.dateTo}
              onChange={(dateTo) => setRunsDraft((prev) => ({ ...prev, dateTo }))}
            />
          </ReconciliationListControls>
          <ReconciliationTableShell
            embedded
            isLoading={runsList.isLoading}
            isEmpty={!runsList.isLoading && runItems.length === 0}
            emptyMessage={t('reconciliation.runs.empty')}
          >
            <Table className={reconciliationRunsTableClass}>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">{t('reconciliation.runs.date')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('reconciliation.runs.revision')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('reconciliation.runs.status')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('reconciliation.runs.completed')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runItems.map((run) => (
                  <TableRow key={run.id} className="cursor-pointer" onClick={() => openRun(run.id)}>
                    <TableCell>{formatDate(run.reportDate)}</TableCell>
                    <TableCell>{run.revision}</TableCell>
                    <TableCell><ReconciliationRunStatusBadge status={run.status} /></TableCell>
                    <TableCell className="whitespace-nowrap">
                      {run.completedAt ? formatDateTime(run.completedAt) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ReconciliationTableShell>
          <ReconciliationTablePagination
            pagination={runsList.pagination}
            onPageChange={setRunsPage}
          />
        </TabsContent>

        <TabsContent value="discrepancies" className="mt-4 space-y-3">
          <ReconciliationListControls onApply={applyCasesFilters} onReset={resetCasesFilters}>
            <ReconciliationFilterSelect
              id="cases-filter-status"
              label={t('reconciliation.filters.status')}
              value={casesDraft.status}
              onChange={(status) => setCasesDraft((prev) => ({ ...prev, status }))}
              options={filterOptions.caseStatusOptions}
            />
            <ReconciliationFilterSelect
              id="cases-filter-type"
              label={t('reconciliation.filters.type')}
              value={casesDraft.type}
              onChange={(type) => setCasesDraft((prev) => ({ ...prev, type }))}
              options={filterOptions.caseTypeOptions}
            />
            <ReconciliationAssetNetworkFilter
              id="cases-filter-asset"
              label={t('reconciliation.filters.asset')}
              value={casesDraft.assetCode}
              options={assetNetworkFilterOptions.assetOptions}
              disabled={currenciesLoading}
              onChange={(assetCode) =>
                setCasesDraft((prev) => ({
                  ...prev,
                  assetCode,
                  networkCode: withAssetNetwork(assetCode, prev.networkCode),
                }))
              }
            />
            <ReconciliationAssetNetworkFilter
              id="cases-filter-network"
              label={t('reconciliation.filters.network')}
              value={casesDraft.networkCode}
              options={assetNetworkFilterOptions.networkOptionsForAsset(casesDraft.assetCode)}
              disabled={currenciesLoading}
              onChange={(networkCode) => setCasesDraft((prev) => ({ ...prev, networkCode }))}
            />
            <ReconciliationAssigneeFilter
              id="cases-filter-assignee"
              label={t('reconciliation.filters.assignee')}
              value={casesDraft.assignee}
              assignees={reconciliationAssignees}
              onChange={(assignee) => setCasesDraft((prev) => ({ ...prev, assignee }))}
            />
          </ReconciliationListControls>
          <ReconciliationTableShell
            embedded
            isLoading={discrepanciesList.isLoading}
            isEmpty={!discrepanciesList.isLoading && caseItems.length === 0}
            emptyMessage={t('reconciliation.discrepancies.empty')}
          >
            <Table className={reconciliationCasesTableClass}>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[9rem]">{t('reconciliation.discrepancy.scope')}</TableHead>
                  <TableHead>{t('reconciliation.discrepancy.asset')}</TableHead>
                  <TableHead>{t('reconciliation.discrepancy.amount')}</TableHead>
                  <TableHead>{t('reconciliation.discrepancy.status')}</TableHead>
                  <TableHead>{t('reconciliation.discrepancy.age')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {caseItems.map((item) => (
                  <TableRow key={item.id} className="cursor-pointer" onClick={() => openCase(item.id)}>
                    <TableCell className="max-w-[10rem] truncate font-mono text-xs">{item.scopeKey}</TableCell>
                    <TableCell>
                      <ReconciliationAssetCell
                        assetCode={item.assetCode}
                        networkCode={item.networkCode}
                        assetIconSrc={resolveCurrencyIconSrc(currencyIconMap, item.assetCode)}
                      />
                    </TableCell>
                    <TableCell className="tabular-nums">{formatLedgerAmount(item.amount, { assetLabel: item.assetCode })}</TableCell>
                    <TableCell>{caseStatusLabel(t, item.status)}</TableCell>
                    <TableCell>{formatReconciliationAge(t, item.ageSeconds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ReconciliationTableShell>
          <ReconciliationTablePagination
            pagination={discrepanciesList.pagination}
            onPageChange={setCasesPage}
          />
        </TabsContent>

        <TabsContent value="suspense" className="mt-4 space-y-3">
          <ReconciliationListControls onApply={applySuspenseFilters} onReset={resetSuspenseFilters}>
            <ReconciliationFilterSelect
              id="suspense-filter-status"
              label={t('reconciliation.filters.status')}
              value={suspenseDraft.status}
              onChange={(status) => setSuspenseDraft((prev) => ({ ...prev, status }))}
              options={filterOptions.suspenseStatusOptions}
            />
            <ReconciliationAssetNetworkFilter
              id="suspense-filter-asset"
              label={t('reconciliation.filters.asset')}
              value={suspenseDraft.assetCode}
              options={assetNetworkFilterOptions.assetOptions}
              disabled={currenciesLoading}
              onChange={(assetCode) =>
                setSuspenseDraft((prev) => ({
                  ...prev,
                  assetCode,
                  networkCode: withAssetNetwork(assetCode, prev.networkCode),
                }))
              }
            />
            <ReconciliationAssetNetworkFilter
              id="suspense-filter-network"
              label={t('reconciliation.filters.network')}
              value={suspenseDraft.networkCode}
              options={assetNetworkFilterOptions.networkOptionsForAsset(suspenseDraft.assetCode)}
              disabled={currenciesLoading}
              onChange={(networkCode) => setSuspenseDraft((prev) => ({ ...prev, networkCode }))}
            />
          </ReconciliationListControls>
          <ReconciliationTableShell
            embedded
            isLoading={suspenseList.isLoading}
            isEmpty={!suspenseList.isLoading && suspenseItems.length === 0}
            emptyMessage={t('reconciliation.suspense.empty')}
          >
            <Table className={reconciliationSuspenseTableClass}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('reconciliation.suspense.asset')}</TableHead>
                  <TableHead>{t('reconciliation.suspense.amount')}</TableHead>
                  <TableHead>{t('reconciliation.suspense.status')}</TableHead>
                  <TableHead>{t('reconciliation.suspense.detected')}</TableHead>
                  {canAssignSuspense ? (
                    <TableHead className="min-w-[8.5rem] whitespace-nowrap">{t('reconciliation.suspense.actions')}</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {suspenseItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <ReconciliationAssetCell
                        assetCode={item.assetCode}
                        networkCode={item.networkCode}
                        assetIconSrc={resolveCurrencyIconSrc(currencyIconMap, item.assetCode)}
                      />
                    </TableCell>
                    <TableCell className="tabular-nums">{formatLedgerAmount(item.amount, { assetLabel: item.assetCode })}</TableCell>
                    <TableCell>
                      <p className="text-sm">{suspenseStatusLabel(t, item.status)}</p>
                      <p className="text-xs text-muted-foreground">{suspenseTypeLabel(t, item.type)}</p>
                    </TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                    {canAssignSuspense ? (
                      <TableCell className="align-top">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full min-w-[7.5rem] whitespace-nowrap sm:w-auto"
                          onClick={() => {
                            setSuspenseId(item.id);
                            setAssignSuspenseOpen(true);
                          }}
                        >
                          {t('reconciliation.suspense.assign')}
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ReconciliationTableShell>
          <ReconciliationTablePagination
            pagination={suspenseList.pagination}
            onPageChange={setSuspensePage}
          />
        </TabsContent>

        <TabsContent value="costs" className="mt-4 space-y-3">
          <ReconciliationListControls onApply={applyCostsFilters} onReset={resetCostsFilters}>
            <ReconciliationFilterSelect
              id="costs-filter-state"
              label={t('reconciliation.filters.state')}
              value={costsDraft.state}
              onChange={(state) => setCostsDraft((prev) => ({ ...prev, state }))}
              options={filterOptions.costStateOptions}
            />
            <ReconciliationFilterSelect
              id="costs-filter-operation"
              label={t('reconciliation.filters.operationType')}
              value={costsDraft.operationType}
              onChange={(operationType) => setCostsDraft((prev) => ({ ...prev, operationType }))}
              options={filterOptions.operationTypeOptions}
            />
            <ReconciliationAssetNetworkFilter
              id="costs-filter-asset"
              label={t('reconciliation.filters.asset')}
              value={costsDraft.assetCode}
              options={assetNetworkFilterOptions.assetOptions}
              disabled={currenciesLoading}
              onChange={(assetCode) => setCostsDraft((prev) => ({ ...prev, assetCode }))}
            />
          </ReconciliationListControls>
          <ReconciliationTableShell
            embedded
            isLoading={costsList.isLoading}
            isEmpty={!costsList.isLoading && costItems.length === 0}
            emptyMessage={t('reconciliation.costs.empty')}
          >
            <Table className={reconciliationCostsTableClass}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('reconciliation.costs.operation')}</TableHead>
                  <TableHead>{t('reconciliation.costs.asset')}</TableHead>
                  <TableHead className="text-right">{t('reconciliation.costs.amount')}</TableHead>
                  <TableHead>{t('reconciliation.costs.payer')}</TableHead>
                  <TableHead>{t('reconciliation.costs.state')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costItems.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>{policyOperationLabel(t, group.operationType)}</TableCell>
                    <TableCell>
                      <ReconciliationAssetCell
                        assetCode={group.assetCode}
                        networkCode={group.networkCode}
                        assetIconSrc={resolveCurrencyIconSrc(currencyIconMap, group.assetCode)}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatLedgerAmount(group.expectedAmount, { assetLabel: group.assetCode })}
                    </TableCell>
                    <TableCell>{policyPayerLabel(t, group.payer)}</TableCell>
                    <TableCell>{costStateLabel(t, group.state)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ReconciliationTableShell>
          <ReconciliationTablePagination
            pagination={costsList.pagination}
            onPageChange={setCostsPage}
          />
        </TabsContent>

        {canManagePolicies ? (
          <TabsContent value="policies" className="mt-4">
            <ReconciliationTableShell
              embedded
              isLoading={policies.isLoading}
              isEmpty={!policies.isLoading && policyItems.length === 0}
              emptyMessage={t('reconciliation.policies.empty')}
            >
              <Table className={reconciliationPoliciesTableClass}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[10rem]">{t('reconciliation.policies.appliesTo')}</TableHead>
                    <TableHead>{t('reconciliation.policies.operation')}</TableHead>
                    <TableHead>{t('reconciliation.policies.network')}</TableHead>
                    <TableHead>{t('reconciliation.policies.payer')}</TableHead>
                    <TableHead>{t('reconciliation.policies.buffer')}</TableHead>
                    <TableHead>{t('reconciliation.policies.status.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {policyItems.map((policy) => (
                    <NetworkCostPolicyTableRow key={policy.scopeKey} policy={policy} />
                  ))}
                </TableBody>
              </Table>
            </ReconciliationTableShell>
          </TabsContent>
        ) : null}
        </Tabs>
      </div>

      <ReconciliationRunDetailDialog
        open={Boolean(runId)}
        onOpenChange={(open) => !open && closeSheet()}
        run={runDetail.data}
        isLoading={Boolean(runId) && !runDetail.data && !runDetail.error}
        rows={runRowsList.items}
        rowsLoading={runRowsList.isLoading}
        rowsPagination={runRowsList.pagination}
        onRowsPageChange={setRunRowsPage}
        filterDraft={rowFiltersDraft}
        onAssetFilterChange={(assetCode) =>
          setRowFiltersDraft((prev) => ({
            ...prev,
            assetCode,
            networkCode: withAssetNetwork(assetCode, prev.networkCode),
          }))
        }
        onNetworkFilterChange={(networkCode) => setRowFiltersDraft((prev) => ({ ...prev, networkCode }))}
        onApplyFilters={applyRowFilters}
        onResetFilters={resetRowFilters}
        assetOptions={assetNetworkFilterOptions.assetOptions}
        networkOptionsForAsset={assetNetworkFilterOptions.networkOptionsForAsset(rowFiltersDraft.assetCode)}
        currenciesLoading={currenciesLoading}
        exportDisabled={busy}
        exportLoading={runExport.isExporting}
        onExport={() => void exportRun()}
      />

      <Sheet open={Boolean(caseId)} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent className="w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] sm:max-w-lg">
          <SheetBody>
            <SheetHeader>
              <SheetTitle>{t('reconciliation.caseDetail.title')}</SheetTitle>
            </SheetHeader>
            {caseDetail.data ? (
              <div className="mt-4 space-y-3">
                <p className="font-mono text-xs break-all">{caseDetail.data.scopeKey}</p>
                <p className="text-xs text-muted-foreground">
                  {t('reconciliation.caseDetail.type')}: {caseTypeLabel(t, caseDetail.data.type)}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <ReconciliationAssetCell
                    assetCode={caseDetail.data.assetCode}
                    networkCode={caseDetail.data.networkCode}
                    assetIconSrc={resolveCurrencyIconSrc(currencyIconMap, caseDetail.data.assetCode)}
                  />
                  <p className="text-sm tabular-nums">
                    {formatLedgerAmount(caseDetail.data.amount, { assetLabel: caseDetail.data.assetCode })}
                  </p>
                </div>
                <p className="text-sm">{caseStatusLabel(t, caseDetail.data.status)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('reconciliation.case.assignee')}:{' '}
                  {caseDetail.data.assignee != null
                    ? formatReconciliationAssigneeLabel(caseDetail.data.assignee, reconciliationAssignees)
                    : t('reconciliation.case.assigneeUnset')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('reconciliation.discrepancy.age')}: {formatReconciliationAge(t, caseDetail.data.ageSeconds)}
                </p>
                {canInvestigate && caseDetail.data.status !== 'RESOLVED' ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" disabled={busy} onClick={openCaseAssign}>
                      {t('reconciliation.case.actions.assign')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      disabled={busy}
                      onClick={() => {
                        setCaseCommentText('');
                        setCaseCommentReason('');
                        setCaseCommentOpen(true);
                      }}
                    >
                      {t('reconciliation.case.actions.comment')}
                    </Button>
                    {isLedgerDifferenceCase(caseDetail.data.type) && parseWalletIdFromScopeKey(caseDetail.data.scopeKey) ? (
                      <Button type="button" variant="secondary" size="sm" className="w-full sm:w-auto" disabled={busy} onClick={openAdjustment}>
                        {t('reconciliation.case.actions.adjustment')}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
                <ul className="space-y-2 text-sm">
                  {(caseDetail.data.decisions ?? []).map((decision) => (
                    <li key={decision.id} className="rounded-xl bg-muted/40 px-3 py-2 sm:rounded-2xl">
                      <p className="font-medium">{decisionActionLabel(t, decision.action)}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(decision.createdAt)}</p>
                      <p>{decision.reason}</p>
                    </li>
                  ))}
                </ul>
                {canResolve && caseDetail.data.status !== 'RESOLVED' ? (
                  <Button type="button" className="w-full sm:w-auto" onClick={() => setResolveOpen(true)} disabled={busy}>
                    {t('reconciliation.resolve.action')}
                  </Button>
                ) : null}
              </div>
            ) : (
              <Skeleton className="mt-4 h-32 w-full" />
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>

      <ReconciliationEnqueueDialog
        open={enqueueOpen}
        reason={enqueueReason}
        loading={busy}
        onOpenChange={setEnqueueOpen}
        onReasonChange={setEnqueueReason}
        onConfirm={() => void enqueueRun()}
      />

      <DiscrepancyAssignDialog
        open={caseAssignOpen}
        loading={busy}
        assignee={caseAssignee}
        reason={caseAssignReason}
        assignees={reconciliationAssignees}
        assigneesLoading={assigneesLoading}
        assigneesError={Boolean(assigneesError)}
        onOpenChange={setCaseAssignOpen}
        onAssigneeChange={setCaseAssignee}
        onReasonChange={setCaseAssignReason}
        onConfirm={() => void assignCase()}
      />

      <DiscrepancyCommentDialog
        open={caseCommentOpen}
        loading={busy}
        text={caseCommentText}
        reason={caseCommentReason}
        onOpenChange={setCaseCommentOpen}
        onTextChange={setCaseCommentText}
        onReasonChange={setCaseCommentReason}
        onConfirm={() => void commentCase()}
      />

      {caseDetail.data ? (
        <DiscrepancyAdjustmentDialog
          open={adjustmentOpen}
          loading={busy}
          caseDetail={caseDetail.data}
          walletId={adjustmentWalletId}
          amount={adjustmentAmount}
          mode={adjustmentMode}
          evidenceRef={adjustmentEvidence}
          reason={adjustmentReason}
          preview={adjustmentPreview}
          canConfirm={canResolve}
          onOpenChange={(open) => {
            setAdjustmentOpen(open);
            if (!open) setAdjustmentPreview(null);
          }}
          onWalletIdChange={(value) => {
            invalidateAdjustmentPreview();
            setAdjustmentWalletId(value);
          }}
          onAmountChange={(value) => {
            invalidateAdjustmentPreview();
            setAdjustmentAmount(value);
          }}
          onModeChange={(mode) => {
            setAdjustmentMode(mode);
            setAdjustmentAmount(adjustmentAmountForMode(caseDetail.data!.amount, mode));
            invalidateAdjustmentPreview();
          }}
          onEvidenceRefChange={(value) => {
            invalidateAdjustmentPreview();
            setAdjustmentEvidence(value);
          }}
          onReasonChange={(value) => {
            invalidateAdjustmentPreview();
            setAdjustmentReason(value);
          }}
          onPreview={() => void previewAdjustment()}
          onConfirm={() => {
            if (!canResolve) return;
            setAdjustmentFactorOpen(true);
          }}
        />
      ) : null}

      <ReasonFactorDialog
        open={adjustmentFactorOpen}
        title={t('reconciliation.case.adjustment.title')}
        description={t('reconciliation.case.adjustment.description')}
        confirmLabel={t('reconciliation.case.adjustment.confirmAction')}
        confirmVariant="destructive"
        loading={busy}
        onOpenChange={(open) => {
          setAdjustmentFactorOpen(open);
          if (!open && adjustmentPreview) setAdjustmentOpen(true);
        }}
        onConfirm={(value) => void confirmAdjustment(value)}
      />

      <ReasonFactorDialog
        open={resolveOpen}
        title={t('reconciliation.resolve.title')}
        description={t('reconciliation.resolve.description')}
        confirmLabel={t('reconciliation.resolve.action')}
        confirmVariant="destructive"
        loading={busy}
        onOpenChange={setResolveOpen}
        onConfirm={(value) => void resolveCase(value)}
      />

      <SuspenseAssignDialog
        open={assignSuspenseOpen}
        loading={busy}
        invoiceId={invoiceId}
        reason={assignReason}
        onOpenChange={(open) => {
          setAssignSuspenseOpen(open);
          if (!open) {
            setInvoiceId('');
            setAssignReason('');
          }
        }}
        onInvoiceIdChange={setInvoiceId}
        onReasonChange={setAssignReason}
        onConfirm={() => {
          setAssignSuspenseOpen(false);
          setAssignFactorOpen(true);
        }}
      />

      <ReasonFactorDialog
        open={assignFactorOpen}
        title={t('reconciliation.suspense.assignTitle')}
        description={t('reconciliation.suspense.assignFactorDescription')}
        confirmLabel={t('reconciliation.suspense.assign')}
        loading={busy}
        onOpenChange={setAssignFactorOpen}
        onConfirm={(value) => void assignSuspense(value)}
      />
    </div>
  );
}
